import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const C = { teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981', warning:'#F59E0B', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA', red:'#EF4444', purple:'#8B5CF6' };

const PACKAGE_LABELS = {
  ai_basic: 'AI Practice Only',
  ai_ielts: 'AI IELTS Prep',
  ai_business: 'AI Business English',
  starter_group: 'Group Classes',
  standard: 'Standard',
  premium: 'Premium Group',
  ielts_intensive: 'IELTS Group Intensive',
  corporate: 'Corporate Group',
  online_group: 'Online Group Classes',
  online_standard: 'Online Standard',
  online_1on1: 'Online Premium Group',
  online_ielts: 'Online IELTS Group',
  trial: 'Trial',
};
const INTENSITY_LABELS  = { starter:'1.5× per week', standard:'2× per week', intensive:'4× per week' };
const INTENSITY_COLORS  = { starter:C.purple, standard:C.teal, intensive:C.red };
const LEVEL_COLORS      = { A1:'#64748B', A2:'#64748B', B1:C.teal, B2:C.teal, C1:'#D97706', C2:'#D97706' };

// Packages that are online/AI — no physical schedule
const AI_PACKAGES = ['ai_basic', 'ai_ielts'];
const isAI = pkg => AI_PACKAGES.includes(pkg);
const LESSON_TYPES_LIST = [
  { id:'grammar', label:'Grammar', icon:'📚', color:'#8B5CF6' },
  { id:'vocabulary', label:'Vocabulary', icon:'📖', color:'#00B4D8' },
  { id:'reading', label:'Reading', icon:'📰', color:'#F59E0B' },
  { id:'writing', label:'Writing', icon:'✍️', color:'#10B981' },
  { id:'conversation', label:'Conversation', icon:'💬', color:'#EF4444' },
  { id:'ielts', label:'IELTS', icon:'🎯', color:'#3B82F6' },
  { id:'business', label:'Business', icon:'💼', color:'#6B7280' },
];




function StartLessonCard({ navigate, courseUnlocked, pkg }) {
  const [selected, setSelected] = React.useState('grammar');
  if (!courseUnlocked) return null;
  return (
    <div style={{ background:'#fff', borderRadius:14, border:'1px solid #E5E7EB', padding:24, marginBottom:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
      <h3 style={{ margin:'0 0 4px', fontSize:17, color:'#1F2937' }}>🎓 Start an AI Practice Session</h3>
      <p style={{ margin:'0 0 16px', fontSize:13, color:'#6B7280' }}>Practise between classes — AI lessons are available any time.</p>
      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
        {LESSON_TYPES_LIST.map(t => (
          <button key={t.id} onClick={() => setSelected(t.id)} style={{
            padding:'8px 14px', borderRadius:20, fontSize:13, fontWeight:600, cursor:'pointer', border:'none', transition:'all 0.15s',
            background: selected===t.id ? t.color : '#F3F4F6',
            color: selected===t.id ? '#fff' : '#6B7280',
          }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      <button onClick={() => navigate('/lesson/' + selected)} style={{
        width:'100%', padding:'14px', background:'#00B4D8', color:'#fff', border:'none',
        borderRadius:10, fontWeight:700, fontSize:15, cursor:'pointer', display:'flex',
        alignItems:'center', justifyContent:'center', gap:8,
      }}>
        ▶ Start Lesson
      </button>
    </div>
  );
}

function NavBar({ navigate }) {
  const nav = [['Dashboard','/dashboard'],['Homework','/homework'],['Vocabulary','/vocabulary'],['My ID','/my-id'],['Certificates','/my-certificates'],['Level Exam','/level-exam'],['My Course','/my-course'],['Payments','/my-payments']];
  return (
    <header style={{ background:C.navy, color:'#fff', padding:'0 32px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
      <div style={{ display:'flex', alignItems:'center', gap:24 }}>
        <span style={{ fontSize:20, fontWeight:700, cursor:'pointer' }} onClick={() => navigate('/dashboard')}>SpeakIQ</span>
        <nav style={{ display:'flex', gap:4 }}>
          {nav.map(([label, path]) => (
            <button key={path} onClick={() => navigate(path)}
              style={{ background:path==='/my-course'?C.teal:'transparent', color:'#fff', border:'none', padding:'6px 12px', borderRadius:6, fontSize:13, fontWeight:path==='/my-course'?700:400, cursor:'pointer' }}>
              {label}
            </button>
          ))}
        </nav>
      </div>
      <button onClick={() => { localStorage.clear(); navigate('/login'); }}
        style={{ background:'transparent', color:'rgba(255,255,255,0.6)', border:'1px solid rgba(255,255,255,0.2)', padding:'6px 14px', borderRadius:6, fontSize:13, cursor:'pointer' }}>
        Logout
      </button>
    </header>
  );
}

function ProgressRing({ percent, size=72, stroke=7, color=C.teal }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (percent / 100) * circ;
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}/>
      <text x={size/2} y={size/2+5} textAnchor="middle" fill={color} fontSize={13} fontWeight={700}>{percent}%</text>
    </svg>
  );
}

function StatBox({ label, value, sub }) {
  return (
    <div style={{ background:'#F8FAFC', borderRadius:8, padding:'12px 14px' }}>
      <div style={{ fontSize:11, color:C.grey, marginBottom:4 }}>{label.toUpperCase()}</div>
      <div style={{ fontWeight:700, color:C.dark, fontSize:15 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:C.grey, marginTop:2 }}>{sub}</div>}
    </div>
  );
}

const REMEDIAL_TIERS = {
  minor:    { label:'Minor Remedial',    emoji:'🟡', color:'#F59E0B', bg:'#FFF7ED', border:'#FED7AA', text:'#92400E' },
  moderate: { label:'Moderate Remedial', emoji:'🟠', color:'#EA580C', bg:'#FFF4E6', border:'#FDBA74', text:'#7C2D12' },
  major:    { label:'Full Repeat',       emoji:'🔴', color:'#EF4444', bg:'#FEF2F2', border:'#FECACA', text:'#7F1D1D' },
};

function RemedialBanner({ remedial, bank }) {
  const t = REMEDIAL_TIERS[remedial.tier] || REMEDIAL_TIERS.moderate;
  const retryDate = remedial.can_retry_after ? new Date(remedial.can_retry_after) : null;
  const strikes = remedial.attempts || 0;
  return (
    <div style={{ background:t.bg, border:'1px solid '+t.border, borderRadius:14, padding:24, marginBottom:20 }}>
      <StrikeBadge strikes={strikes}/>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
        <span style={{ fontSize:32 }}>{t.emoji}</span>
        <div>
          <div style={{ fontWeight:700, fontSize:16, color:t.text }}>{t.label} Required</div>
          <div style={{ fontSize:13, color:t.text, opacity:0.8 }}>Level {remedial.level} — Score: {remedial.score?.toFixed(0)}%</div>
        </div>
        <div style={{ marginLeft:'auto', background:t.color, color:'#fff', fontWeight:700, fontSize:20, padding:'6px 16px', borderRadius:10 }}>
          {remedial.score?.toFixed(0)}%
        </div>
      </div>

      <p style={{ fontSize:14, color:t.text, margin:'0 0 16px', lineHeight:1.6 }}>
        {remedial.tier === 'minor' && `You're very close to passing! Just ${remedial.sessions} more focused sessions (${remedial.weeks} weeks) targeting your weak areas.`}
        {remedial.tier === 'moderate' && `You need ${remedial.sessions} targeted sessions (${remedial.weeks} weeks) to strengthen your English at this level before moving up.`}
        {remedial.tier === 'major' && `We recommend repeating Level ${remedial.level} fully. ${remedial.sessions} sessions (${remedial.weeks} weeks) to build a solid foundation.`}
      </p>

      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        {[
          ['Extra Sessions', remedial.sessions],
          ['Duration', `${remedial.weeks}w`],
          ['Extra Hours', `${remedial.hours}h`],
          ['Attempts', remedial.attempts],
        ].map(([k,v]) => (
          <div key={k} style={{ background:'rgba(255,255,255,0.6)', borderRadius:8, padding:'8px 14px', flex:1, minWidth:90 }}>
            <div style={{ fontSize:11, color:t.text, opacity:0.7 }}>{k.toUpperCase()}</div>
            <div style={{ fontWeight:700, color:t.text, fontSize:15 }}>{v}</div>
          </div>
        ))}
      </div>

      {!remedial.paid ? (
        <div style={{ background:'rgba(255,255,255,0.7)', borderRadius:10, padding:16 }}>
          <p style={{ fontWeight:600, fontSize:13, color:t.text, marginBottom:8 }}>💳 Pay for your remedial course to continue:</p>
          {bank?.account_number && (
            <div style={{ fontSize:13 }}>
              {[['Bank',bank.bank_name],['Account',bank.account_number],['Sort Code',bank.sort_code],['Reference',`REMEDIAL-${remedial.level}`]].map(([k,v]) => v && (
                <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'3px 0', borderBottom:'1px solid rgba(0,0,0,0.06)' }}>
                  <span style={{ color:t.text, opacity:0.7 }}>{k}</span><strong style={{ color:t.text }}>{v}</strong>
                </div>
              ))}
            </div>
          )}
          <p style={{ fontSize:12, color:t.text, opacity:0.7, marginTop:8, marginBottom:0 }}>
            Upload your payment proof on the Dashboard, then your school will confirm and unlock your remedial course.
          </p>
        </div>
      ) : (
        <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:12, fontSize:13, color:'#166534' }}>
          ✅ Remedial payment confirmed. Your course has been extended. Complete the extra sessions then retake the Level Exam.
          {retryDate && <span> Retry available from <strong>{retryDate.toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</strong>.</span>}
        </div>
      )}
    </div>
  );
}

function LockedBanner({ examStatus }) {
  const strikes = examStatus?.strike_count || 0;
  return (
    <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:14, padding:28, textAlign:'center', marginBottom:20 }}>
      <div style={{ fontSize:52, marginBottom:12 }}>🔐</div>
      <h3 style={{ color:'#7F1D1D', margin:'0 0 8px' }}>Course Paused — Admin Review Required</h3>
      <p style={{ color:'#991B1B', fontSize:14, marginBottom:16, lineHeight:1.6 }}>
        You have attempted this level <strong>{strikes} times</strong>. Your school has been notified and will review your progress.
        <br/>A teacher will contact you to discuss the best way forward.
      </p>
      <div style={{ display:'flex', justifyContent:'center', gap:8, marginBottom:16 }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ width:36, height:36, borderRadius:'50%', background: i <= strikes ? '#EF4444' : '#FEE2E2', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>
            {i <= strikes ? '✗' : '○'}
          </div>
        ))}
      </div>
      <p style={{ fontSize:13, color:'#B91C1C' }}>
        {examStatus?.locked_reason}
      </p>
      <div style={{ marginTop:16, background:'rgba(255,255,255,0.6)', borderRadius:8, padding:12, fontSize:13, color:'#7F1D1D' }}>
        📞 Please contact your school directly to discuss your options. Your school may offer additional support sessions or a personalised plan.
      </div>
    </div>
  );
}

function StrikeBadge({ strikes }) {
  if (!strikes || strikes === 0) return null;
  const colors = { 1:'#F59E0B', 2:'#EA580C', 3:'#EF4444' };
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:12 }}>
      <span style={{ fontSize:13, color:'#6B7280' }}>Attempts at this level:</span>
      {[1,2,3].map(i => (
        <div key={i} style={{ width:24, height:24, borderRadius:'50%', background: i <= strikes ? colors[Math.min(strikes,3)] : '#E5E7EB', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, color: i <= strikes ? '#fff' : '#9CA3AF', fontWeight:700 }}>
          {i <= strikes ? '✗' : i}
        </div>
      ))}
      {strikes === 2 && <span style={{ fontSize:12, color:'#EA580C', fontWeight:600 }}>⚠️ Final attempt — course escalated</span>}
    </div>
  );
}

// ─── AI Course View ──────────────────────────────────────────────────────────
function AICourseView({ sched, courseInfo }) {
  return (
    <>
      <div style={{ background:'#fff', borderRadius:14, border:'1px solid '+C.border, padding:24, marginBottom:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:16 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap' }}>
              <span style={{ background:LEVEL_COLORS[sched.level]||C.teal, color:'#fff', fontWeight:700, fontSize:13, padding:'3px 10px', borderRadius:6 }}>{sched.level}</span>
              <span style={{ background:'#EDE9FE', color:'#5B21B6', fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:6, border:'1px solid #C4B5FD' }}>🤖 {PACKAGE_LABELS[sched.package] || sched.package}</span>
            </div>
            <h2 style={{ margin:'0 0 6px', fontSize:20, color:C.dark }}>{courseInfo.title || `AI English — ${sched.level}`}</h2>
            <p style={{ margin:'0 0 12px', fontSize:14, color:C.grey, lineHeight:1.5 }}>
              {courseInfo.description || 'AI-powered personalised lessons tailored to your level. Learn at your own pace — any time, anywhere.'}
            </p>
            <div style={{ display:'flex', gap:16, flexWrap:'wrap', fontSize:13 }}>
              <span>🤖 <strong>AI-powered lessons</strong></span>
              <span>📱 <strong>Learn anytime</strong></span>
              <span>🎯 <strong>Level {sched.level}</strong></span>
            </div>
          </div>
        </div>

        {courseInfo.url && (
          <a href={courseInfo.url} target="_blank" rel="noopener noreferrer"
            style={{ display:'inline-block', marginTop:16, background:C.teal, color:'#fff', padding:'10px 20px', borderRadius:8, fontWeight:600, fontSize:14, textDecoration:'none' }}>
            Open Course Materials →
          </a>
        )}
      </div>

      <div style={{ background:'#fff', borderRadius:14, border:'1px solid '+C.border, padding:24 }}>
        <h3 style={{ margin:'0 0 16px', fontSize:17, color:C.dark }}>🎓 AI Learning Path</h3>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12 }}>
          <StatBox label="Course Type"   value="AI-Powered"  sub="Online, self-paced" />
          <StatBox label="Current Level" value={sched.level} sub="Personalised lessons" />
          <StatBox label="Sessions"      value="Unlimited"   sub="Access anytime" />
          <StatBox label="Lesson Length" value="Flexible"    sub="Your own pace" />
        </div>
        <div style={{ marginTop:16, padding:'12px 16px', background:'#F5F3FF', borderRadius:8, fontSize:13, color:'#5B21B6' }}>
          🏆 Complete your AI lessons and take the <strong>Level Exam</strong> to progress to the next level.
        </div>
      </div>
    </>
  );
}

// ─── Physical Course View ────────────────────────────────────────────────────
function PhysicalCourseView({ sched, courseInfo }) {
  const startDate = sched.start_date ? new Date(sched.start_date) : null;
  const endDate   = sched.end_date   ? new Date(sched.end_date)   : null;
  const roadmap   = sched.roadmap    || [];
  const intensity = sched.intensity  || 'standard';
  const intensityColor = INTENSITY_COLORS[intensity] || C.teal;

  let progressPct = 0;
  if (startDate && endDate) {
    const total   = endDate - startDate;
    const elapsed = Date.now() - startDate;
    progressPct   = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
  }
  const weeksRemaining = endDate ? Math.max(0, Math.ceil((endDate - Date.now()) / (7*24*3600*1000))) : null;

  return (
    <>
      {/* Current Course Card */}
      <div style={{ background:'#fff', borderRadius:14, border:'1px solid '+C.border, padding:24, marginBottom:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:16 }}>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap' }}>
              <span style={{ background:LEVEL_COLORS[sched.level]||C.teal, color:'#fff', fontWeight:700, fontSize:13, padding:'3px 10px', borderRadius:6 }}>{sched.level}</span>
              <span style={{ background:'#F0FDF4', color:'#166534', fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:6, border:'1px solid #BBF7D0' }}>{PACKAGE_LABELS[sched.package]||sched.package}</span>
              <span style={{ background:intensityColor+'20', color:intensityColor, fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:6 }}>{intensity.toUpperCase()}</span>
            </div>
            <h2 style={{ margin:'0 0 4px', fontSize:20, color:C.dark }}>{courseInfo.title || `English ${sched.level} Course`}</h2>
            {courseInfo.description && <p style={{ margin:'0 0 12px', fontSize:14, color:C.grey, lineHeight:1.5 }}>{courseInfo.description}</p>}
            <div style={{ display:'flex', gap:20, flexWrap:'wrap', fontSize:13 }}>
              <span>📅 <strong>{INTENSITY_LABELS[intensity]}</strong></span>
              <span>⏱️ <strong>90 min</strong> per session</span>
              {sched.total_sessions > 0 && <span>🎯 <strong>{sched.total_sessions}</strong> total sessions</span>}
            </div>
          </div>
          {(startDate && endDate) && <ProgressRing percent={progressPct} color={intensityColor}/>}
        </div>

        {/* Dates */}
        {(startDate || endDate) && (
          <div style={{ display:'flex', gap:12, marginTop:16, flexWrap:'wrap' }}>
            {startDate && (
              <div style={{ flex:1, minWidth:140, background:'#F8FAFC', borderRadius:8, padding:'10px 14px' }}>
                <div style={{ fontSize:11, color:C.grey, marginBottom:2 }}>COURSE STARTED</div>
                <div style={{ fontWeight:700, color:C.dark }}>{startDate.toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>
              </div>
            )}
            {endDate && (
              <div style={{ flex:1, minWidth:140, background:weeksRemaining<=4?'#FFF7ED':'#F0FDF4', borderRadius:8, padding:'10px 14px' }}>
                <div style={{ fontSize:11, color:C.grey, marginBottom:2 }}>COURSE ENDS</div>
                <div style={{ fontWeight:700, color:weeksRemaining<=4?'#92400E':'#166534' }}>
                  {endDate.toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}
                  {weeksRemaining!==null && <span style={{ fontSize:12, fontWeight:400, marginLeft:6 }}>({weeksRemaining}w left)</span>}
                </div>
              </div>
            )}
          </div>
        )}

        {courseInfo.url && (
          <a href={courseInfo.url} target="_blank" rel="noopener noreferrer"
            style={{ display:'inline-block', marginTop:16, background:C.teal, color:'#fff', padding:'10px 20px', borderRadius:8, fontWeight:600, fontSize:14, textDecoration:'none' }}>
            Open Course Materials →
          </a>
        )}
      </div>

      {/* Roadmap */}
      {roadmap.length > 0 && (
        <div style={{ background:'#fff', borderRadius:14, border:'1px solid '+C.border, padding:24, marginBottom:20 }}>
          <h3 style={{ margin:'0 0 6px', fontSize:17, color:C.dark }}>🗺️ Your Learning Roadmap</h3>
          <p style={{ margin:'0 0 20px', fontSize:13, color:C.grey }}>Complete each level to unlock the next. Your ID card renews automatically.</p>
          <div style={{ position:'relative' }}>
            <div style={{ position:'absolute', left:20, top:0, bottom:0, width:2, background:C.border, zIndex:0 }}/>
            {roadmap.map((seg, idx) => {
              const isCurrent = seg.level === sched.level;
              const segEnd    = new Date(seg.end_date);
              return (
                <div key={seg.level} style={{ display:'flex', gap:16, marginBottom:idx<roadmap.length-1?20:0, position:'relative', zIndex:1 }}>
                  <div style={{ width:42, height:42, borderRadius:'50%', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, border:`3px solid ${isCurrent?LEVEL_COLORS[seg.level]||C.teal:C.border}`, background:isCurrent?LEVEL_COLORS[seg.level]||C.teal:'#fff', color:isCurrent?'#fff':C.grey }}>
                    {seg.level}
                  </div>
                  <div style={{ flex:1, paddingTop:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <span style={{ fontWeight:700, color:isCurrent?C.dark:C.grey }}>
                        {isCurrent ? '▶ Current Level' : `Level ${seg.level}`}
                        {seg.next_level && <span style={{ fontWeight:400, color:C.grey }}> → {seg.next_level}</span>}
                      </span>
                      {isCurrent && <span style={{ background:C.teal+'20', color:C.teal, fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10 }}>IN PROGRESS</span>}
                    </div>
                    <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>
                      {seg.sessions} sessions · {seg.weeks}w · ends {segEnd.toLocaleDateString('en-GB',{month:'short',year:'numeric'})}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop:20, padding:'12px 16px', background:'#F0FDF4', borderRadius:8, fontSize:13, color:'#166534' }}>
            🏆 After completing each level, take the <strong>Level Exam</strong> to unlock your next course. Your ID card and enrolment renew automatically.
          </div>
        </div>
      )}

      {/* Schedule Summary */}
      <div style={{ background:'#fff', borderRadius:14, border:'1px solid '+C.border, padding:24 }}>
        <h3 style={{ margin:'0 0 16px', fontSize:17, color:C.dark }}>📋 Course Schedule</h3>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12 }}>
          <StatBox label="Sessions/week"  value={INTENSITY_LABELS[intensity]} />
          <StatBox label="Session length" value="90 minutes" />
          <StatBox label="Course type"    value={intensity.charAt(0).toUpperCase()+intensity.slice(1)} />
          <StatBox label="Total sessions" value={sched.total_sessions > 0 ? sched.total_sessions : '—'} />
        </div>
      </div>
    </>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function StudentCourse() {
  const [info, setInfo]       = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/student/payment-info')
      .then(r => setInfo(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ minHeight:'100vh', background:C.bg }}>
      <NavBar navigate={navigate}/>
      <div style={{ textAlign:'center', padding:80, color:C.grey }}>Loading your course…</div>
    </div>
  );

  const unlocked   = info?.course_unlocked;
  const sched      = info?.course_schedule || {};
  const courseInfo = info?.course_info     || {};
  const bank       = info?.bank_details    || {};
  const status     = info?.payment_status;
  const pkg        = sched.package || '';

  return (
    <div style={{ minHeight:'100vh', background:C.bg }}>
      <NavBar navigate={navigate}/>
      <div style={{ maxWidth:700, margin:'0 auto', padding:'40px 16px' }}>

        <h1 style={{ fontSize:24, fontWeight:700, color:C.dark, marginBottom:4 }}>My Course</h1>
        <p style={{ color:C.grey, fontSize:14, marginBottom:28 }}>Your enrolled course and learning roadmap</p>

        {/* ── Locked ─────────────────────────────────────────────────────── */}
        {!unlocked ? (
          status === 'proof_submitted' ? (
            <div style={{ background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:14, padding:32, textAlign:'center' }}>
              <div style={{ fontSize:52, marginBottom:12 }}>⏳</div>
              <h3 style={{ color:'#92400E', margin:'0 0 8px' }}>Payment Under Review</h3>
              <p style={{ color:'#78350F', fontSize:14 }}>Your payment proof has been submitted. Your course unlocks once your school confirms payment — usually within 24 hours.</p>
            </div>
          ) : (
            <div style={{ background:'#fff', border:'1px solid '+C.border, borderRadius:14, padding:32 }}>
              <div style={{ fontSize:52, textAlign:'center', marginBottom:12 }}>🔒</div>
              <h3 style={{ textAlign:'center', color:C.dark, marginBottom:6 }}>Course Locked</h3>
              <p style={{ textAlign:'center', color:C.grey, fontSize:14, marginBottom:24 }}>Complete payment to unlock your course and receive your personalised learning roadmap.</p>
              {bank.local_payment && (
                <div style={{ background:'#ECFDF5', border:'2px solid #10B981', borderRadius:10, padding:16, marginBottom:12 }}>
                  <p style={{ fontSize:13, fontWeight:800, color:'#065F46', marginBottom:6 }}>💳 Pay by local card — fastest</p>
                  <p style={{ fontSize:13, fontWeight:600, color:C.dark, margin:0 }}>{bank.local_payment}</p>
                </div>
              )}
              {bank.account_number && (
                <div style={{ background:'#F8FAFC', border:'1px solid '+C.border, borderRadius:10, padding:16 }}>
                  <p style={{ fontSize:13, fontWeight:600, color:C.dark, marginBottom:10 }}>Bank Transfer Details:</p>
                  {[['Bank',bank.bank_name],['Account Holder',bank.account_holder],['Account Number',bank.account_number],['Sort Code',bank.sort_code]].map(([k,v]) => v && (
                    <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'4px 0', borderBottom:'1px solid #F1F5F9' }}>
                      <span style={{ color:C.grey }}>{k}</span><strong>{v}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        ) : (
          <>
            {info?.exam_status?.course_locked
              ? <LockedBanner examStatus={info.exam_status}/>
              : info?.remedial?.active && (
                  <RemedialBanner remedial={info.remedial} bank={info.bank_details || {}}/>
                )
            }
            {isAI(pkg)
              ? <AICourseView sched={sched} courseInfo={courseInfo}/>
              : <PhysicalCourseView sched={sched} courseInfo={courseInfo}/>
            }
            <StartLessonCard navigate={navigate} courseUnlocked={unlocked} pkg={pkg} />
          </>
        )}

      </div>
    </div>
  );
}
